import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Home, Save, FileText, Smartphone, User, Phone, Mail, X,
  Sparkles, Check, RotateCcw, Eye, Download, Loader2, Clock, Euro, Wrench,
  AlertTriangle, FileWarning, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function TechnicalDocFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const docType = location.pathname.includes('irreparability') ? 'irreparability' : 'diagnostic';
  const isEdit = !!id;

  const token = localStorage.getItem('token');

  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    device_brand: '',
    device_model: '',
    device_serial: '',
    device_imei: '',
    reported_problem: '',
    // Diagnostic fields
    diagnostic_findings: '',
    diagnostic_findings_ai: '',
    recommended_repairs: '',
    recommended_repairs_ai: '',
    estimated_cost: '',
    estimated_duration: '',
    // Irreparability fields
    technical_analysis: '',
    technical_analysis_ai: '',
    irreparability_reasons: '',
    irreparability_reasons_ai: '',
    attempted_repairs: '',
    attempted_repairs_ai: '',
    technician_conclusion: '',
    technician_conclusion_ai: '',
    // Common
    use_ai_version: false,
    status: 'draft'
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [improvingField, setImprovingField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // AI improved fields tracking
  const [aiImprovedFields, setAiImprovedFields] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchDocument();
    }
  }, [id]);

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const endpoint = docType === 'diagnostic' 
        ? `${API}/api/technical-docs/diagnostics/${id}`
        : `${API}/api/technical-docs/irreparability/${id}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
      
      // Track which fields have AI versions
      const improved = {};
      if (response.data.diagnostic_findings_ai) improved.diagnostic_findings = true;
      if (response.data.recommended_repairs_ai) improved.recommended_repairs = true;
      if (response.data.technical_analysis_ai) improved.technical_analysis = true;
      if (response.data.irreparability_reasons_ai) improved.irreparability_reasons = true;
      if (response.data.attempted_repairs_ai) improved.attempted_repairs = true;
      if (response.data.technician_conclusion_ai) improved.technician_conclusion = true;
      setAiImprovedFields(improved);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const improveWithAI = async (field, fieldType) => {
    const text = formData[field];
    if (!text || text.trim().length < 10) {
      toast.error('Le texte doit contenir au moins 10 caractères');
      return;
    }

    setImprovingField(field);
    try {
      const response = await axios.post(`${API}/api/technical-docs/improve-text`, {
        text: text,
        field_type: fieldType,
        document_type: docType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const aiField = `${field}_ai`;
      setFormData(prev => ({ 
        ...prev, 
        [aiField]: response.data.improved 
      }));
      setAiImprovedFields(prev => ({ ...prev, [field]: true }));
      toast.success('Texte amélioré par l\'IA !');
    } catch (error) {
      toast.error('Erreur lors de l\'amélioration');
      console.error(error);
    } finally {
      setImprovingField(null);
    }
  };

  const selectAIVersion = (field) => {
    const aiField = `${field}_ai`;
    if (formData[aiField]) {
      setFormData(prev => ({ ...prev, use_ai_version: true }));
      toast.success('Version IA sélectionnée');
    }
  };

  const selectOriginalVersion = (field) => {
    setFormData(prev => ({ ...prev, use_ai_version: false }));
    toast.info('Version originale sélectionnée');
  };

  const handleSave = async (status = 'draft') => {
    if (!formData.client_name || !formData.device_brand || !formData.device_model) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      // Clean data - convert empty strings to null for optional fields
      const data = { 
        ...formData, 
        status,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null
      };
      const endpoint = docType === 'diagnostic'
        ? `${API}/api/technical-docs/diagnostics${isEdit ? `/${id}` : ''}`
        : `${API}/api/technical-docs/irreparability${isEdit ? `/${id}` : ''}`;
      
      const method = isEdit ? 'put' : 'post';
      const response = await axios[method](endpoint, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(status === 'finalized' ? 'Document finalisé !' : 'Document sauvegardé');
      
      if (!isEdit) {
        // Redirect to edit mode with new ID
        const newId = response.data.id;
        navigate(`/technical-docs/${docType}/${newId}`, { replace: true });
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const viewPDF = () => {
    if (!id) {
      toast.error('Veuillez d\'abord sauvegarder le document');
      return;
    }
    const endpoint = docType === 'diagnostic'
      ? `${API}/api/technical-docs/diagnostics/${id}/pdf`
      : `${API}/api/technical-docs/irreparability/${id}/pdf`;
    window.open(endpoint + `?token=${token}`, '_blank');
  };

  const renderAIField = (field, label, fieldType, placeholder, isTextarea = true) => {
    const aiField = `${field}_ai`;
    const hasAI = aiImprovedFields[field] && formData[aiField];
    const isImproving = improvingField === field;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-white flex items-center gap-2">
            {label}
            {hasAI && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                IA disponible
              </span>
            )}
          </label>
          <Button
            type="button"
            size="sm"
            onClick={() => improveWithAI(field, fieldType)}
            disabled={isImproving || !formData[field]}
            className="bg-purple-500 hover:bg-purple-600 text-white h-7 text-xs"
          >
            {isImproving ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Amélioration...</>
            ) : (
              <><Sparkles className="w-3 h-3 mr-1" /> Améliorer avec IA</>
            )}
          </Button>
        </div>

        {/* Original text input */}
        <div className="relative">
          {isTextarea ? (
            <Textarea
              value={formData[field] || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder={placeholder}
              className="bg-white/10 border-white/20 text-white min-h-[100px]"
              data-testid={`field-${field}`}
            />
          ) : (
            <Input
              value={formData[field] || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder={placeholder}
              className="bg-white/10 border-white/20 text-white"
            />
          )}
        </div>

        {/* AI improved version */}
        {hasAI && (
          <div className="bg-purple-900/30 rounded-lg border border-purple-500/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Version améliorée par l'IA
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => selectAIVersion(field)}
                  className={`h-7 text-xs ${formData.use_ai_version ? 'bg-emerald-500' : 'bg-purple-500 hover:bg-purple-600'}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  {formData.use_ai_version ? 'Validé IA' : 'Valider IA'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => selectOriginalVersion(field)}
                  className="h-7 text-xs border-white/20 text-white hover:bg-white/10"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Garder original
                </Button>
              </div>
            </div>
            <div className="text-sm text-purple-100 whitespace-pre-wrap bg-purple-950/50 rounded p-3">
              {formData[aiField]}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/invoices', { state: { activeTab: 'documents' } })}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
              data-testid="back-to-documents-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Retour aux documents
            </Button>
            <div className="flex items-center gap-2">
              {docType === 'diagnostic' ? (
                <FileText className="w-6 h-6 text-blue-400" />
              ) : (
                <FileWarning className="w-6 h-6 text-red-400" />
              )}
              <h1 className="text-lg font-bold text-white">
                {docType === 'diagnostic' ? 'Rapport de Diagnostic' : 'Attestation d\'Irréparabilité'}
                {isEdit && formData.number && ` - ${formData.number}`}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEdit && (
              <Button
                onClick={viewPDF}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Eye className="w-4 h-4 mr-2" />
                Aperçu PDF
              </Button>
            )}
            <Button
              onClick={() => handleSave('draft')}
              disabled={saving}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Brouillon
            </Button>
            <Button
              onClick={() => handleSave('finalized')}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Finaliser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status badge */}
        {formData.status && (
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
            formData.status === 'finalized' 
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          }`}>
            {formData.status === 'finalized' ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            {formData.status === 'finalized' ? 'Finalisé' : 'Brouillon'}
          </div>
        )}

        {/* Client & Device Info */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Client Info */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Informations Client
            </h3>
            
            <div>
              <label className="text-sm text-white/70">Nom *</label>
              <Input
                value={formData.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                placeholder="Nom du client"
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="client-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/70">Téléphone</label>
                <Input
                  value={formData.client_phone}
                  onChange={(e) => handleChange('client_phone', e.target.value)}
                  placeholder="06 XX XX XX XX"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-white/70">Email</label>
                <Input
                  value={formData.client_email}
                  onChange={(e) => handleChange('client_email', e.target.value)}
                  placeholder="email@exemple.com"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
            </div>
          </div>

          {/* Device Info */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-emerald-400" />
              Appareil
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/70">Marque *</label>
                <Input
                  value={formData.device_brand}
                  onChange={(e) => handleChange('device_brand', e.target.value)}
                  placeholder="Apple, Samsung..."
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="device-brand"
                />
              </div>
              <div>
                <label className="text-sm text-white/70">Modèle *</label>
                <Input
                  value={formData.device_model}
                  onChange={(e) => handleChange('device_model', e.target.value)}
                  placeholder="iPhone 13, Galaxy S21..."
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="device-model"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/70">N° Série</label>
                <Input
                  value={formData.device_serial}
                  onChange={(e) => handleChange('device_serial', e.target.value)}
                  placeholder="Numéro de série"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-white/70">IMEI</label>
                <Input
                  value={formData.device_imei}
                  onChange={(e) => handleChange('device_imei', e.target.value)}
                  placeholder="IMEI de l'appareil"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Problem Reported */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-4">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Problème Signalé par le Client
          </h3>
          <Textarea
            value={formData.reported_problem}
            onChange={(e) => handleChange('reported_problem', e.target.value)}
            placeholder="Décrivez le problème tel que signalé par le client..."
            className="bg-white/10 border-white/20 text-white min-h-[80px]"
            data-testid="reported-problem"
          />
        </div>

        {/* Diagnostic specific fields */}
        {docType === 'diagnostic' && (
          <>
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              {renderAIField(
                'diagnostic_findings',
                'Constatations du Technicien',
                'diagnostic',
                'Décrivez vos constatations techniques suite à l\'examen de l\'appareil...'
              )}
            </div>

            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              {renderAIField(
                'recommended_repairs',
                'Réparations Recommandées',
                'repairs',
                'Listez les réparations nécessaires pour remettre l\'appareil en état...'
              )}
            </div>

            {/* Cost estimation */}
            <div className="bg-emerald-900/30 rounded-xl border border-emerald-500/30 p-4 space-y-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Euro className="w-5 h-5 text-emerald-400" />
                Estimation
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70">Coût estimé (€ TTC)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => handleChange('estimated_cost', e.target.value)}
                    placeholder="0.00"
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70">Durée estimée</label>
                  <Input
                    value={formData.estimated_duration}
                    onChange={(e) => handleChange('estimated_duration', e.target.value)}
                    placeholder="Ex: 2-3 jours"
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Irreparability specific fields */}
        {docType === 'irreparability' && (
          <>
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              {renderAIField(
                'technical_analysis',
                'Analyse Technique',
                'analysis',
                'Décrivez l\'analyse technique détaillée de l\'appareil...'
              )}
            </div>

            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              {renderAIField(
                'attempted_repairs',
                'Tentatives de Réparation Effectuées',
                'repairs_attempted',
                'Décrivez les tentatives de réparation effectuées (optionnel)...'
              )}
            </div>

            <div className="bg-red-900/30 rounded-xl border border-red-500/30 p-4">
              {renderAIField(
                'irreparability_reasons',
                'Raisons de l\'Irréparabilité',
                'reasons',
                'Expliquez pourquoi l\'appareil ne peut pas être réparé...'
              )}
            </div>

            <div className="bg-red-900/50 rounded-xl border border-red-500/50 p-4">
              {renderAIField(
                'technician_conclusion',
                'Conclusion du Technicien',
                'conclusion',
                'Rédigez la conclusion finale attestant de l\'irréparabilité...'
              )}
            </div>
          </>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <Button
            onClick={() => navigate('/invoices', { state: { activeTab: 'documents' } })}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="cancel-diagnostic-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
          <Button
            onClick={() => handleSave('draft')}
            disabled={saving}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
          >
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder brouillon
          </Button>
          <Button
            onClick={() => handleSave('finalized')}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Finaliser le document
          </Button>
        </div>
      </main>
    </div>
  );
}
